import { Component, Input, OnInit, signal, WritableSignal } from "@angular/core";
import { Measurement } from "@evident/ngx-media-viewer";
import { TranslateService } from "@ngx-translate/core";
import { TenantConfigService } from "../../../../../_services/http-api/tenant-config.service";
import { MeasurementModeService } from "../../../../../_services/measurement-mode.service";
import { MerryMeasurementItems } from "../../../../../_services/merry-measurement-items";
import { MerryMeasurementParserService } from "../../../../../_services/merry-measurement-parser.service";
import { InspectionImageOfflineService } from "../../../../../_services/offline/inspection-image-offline.service";
import { TenantConfig, USE_IMAGE_TYPE } from "../../../../../app.constants";
import * as Measure from "../../../../../inspection-data-management/image-edit-dialog/measurement-merry/measurement.type";
import { PreviewImageDetail } from "../../../../models/report-preview/preview-image-detail";
import { LegacyReportMeasurementService, ReportMeasurementResult } from "../../../../services/legacy-report-measurement.service";

/** Labels of the stored modes the legacy translation table (imageRemeasurementEdit) does not know. */
const MODE_LABEL_KEYS: Record<string, string> = {
  tipdefect: "measurement.TipDefect",
  missingcorner: "measurement.TipDefect",
  polyline: "measurement.Polyline"
};

@Component({
  selector: "app-report-measurement",
  templateUrl: "./report-measurement.component.html",
  styleUrl: "./report-measurement.component.scss",
  standalone: false
})
export class ReportMeasurementComponent implements OnInit {
  @Input({ required: true }) image: PreviewImageDetail | null = null;
  legacyMeasurementResults: ReportMeasurementResult[] = [];
  merryMeasurementResults: WritableSignal<Measurement[]> = signal([]);
  unitSystem: Measure.UnitSystem = "Metric";

  constructor(
    private translate: TranslateService,
    private legacyMeasurementService: LegacyReportMeasurementService,
    private merryMeasurementParser: MerryMeasurementParserService,
    private inspectionImageService: InspectionImageOfflineService,
    private measurementModeService: MeasurementModeService,
    private tenantConfigService: TenantConfigService
  ) {}

  ngOnInit() {
    this.init();
  }

  /**
   * The unit is the one the measurements were saved with (both measurement tabs store it on every item), else the
   * tenant default. A Merry image shows the measurements of its file (ViSOL and Merry both write them there) and falls
   * back to its stored items when the file holds none or cannot be read, like every other image.
   */
  private async init() {
    if (!this.image) {
      return;
    }
    this.unitSystem = MerryMeasurementItems.unitSystemOf(this.image.measurementItems) ?? this.tenantDefaultUnitSystem();

    if (this.measurementModeService.isMerryMeasurementSupported(this.image.imageType)) {
      const measurements = await this.readMerryMeasurements();
      if (measurements.length) {
        this.merryMeasurementResults.set(measurements);
        return;
      }
    }
    this.setLegacyMeasurements();
  }

  private setLegacyMeasurements() {
    this.legacyMeasurementResults = this.legacyMeasurementService.toReportMeasurementResult(this.image?.measurementItems ?? []);
  }

  private async readMerryMeasurements(): Promise<Measurement[]> {
    try {
      const url = await this.inspectionImageService.getImageBinaryUrl(this.image.inspectionId, this.image.id, USE_IMAGE_TYPE.ORIGINAL);
      return await this.merryMeasurementParser.parseMeasurementResults(url, { importLegacy: false });
    } catch (error) {
      console.error(`[ReportMeasurement] ${this.image.fileName}: the measurements of the file could not be read`, error);
      return [];
    }
  }

  public precision() {
    return Measure.precision(this.unitSystem);
  }

  public getUnitLabel(unit: Measure.UnitType): string {
    return Measure.getUnitLabel(unit, this.unitSystem);
  }

  public getResults(measurement: Measurement): Measure.MeasurementResultItem[] {
    return Measure.getMeasurementResults(measurement);
  }

  public formatLegacyMeasurement(item: ReportMeasurementResult): string {
    const measurementNumber = item?.id.split(":")[0];

    const isArea = this.legacyMeasurementService.isAreaMode(item.mode);
    const result = isArea ? item.result.area : item.result.length;
    const unit = isArea ? `${item.unit}^2` : item.unit;

    return `${measurementNumber}: ${this.modeLabel(item.mode)} ${result} ${unit}`;
  }

  private modeLabel(mode: string): string {
    return this.translate.instant(MODE_LABEL_KEYS[mode.toLowerCase()] ?? `imageRemeasurementEdit.${mode}`);
  }

  private tenantDefaultUnitSystem(): Measure.UnitSystem {
    return Measure.unitSystemOfUnitLabel(this.tenantConfigService.getConfigBykey(TenantConfig.DefaultUnitOfMeasurementResult.KEY));
  }
}
